import * as React from 'react';
import { forwardRef, useState } from 'react';
import TextareaAutosize, { TextareaAutosizeProps } from 'react-textarea-autosize';
import styled from 'styled-components';
import { SecondaryText } from 'styles/theme_styles/text';
import { tokens } from 'styles/tokens';
import { mergeRefs } from 'utils/mergeRefs';

const StyledTextarea = styled(TextareaAutosize)`
    box-sizing: border-box;
    padding: 1rem;
    width: 100%;
    resize: vertical;
    min-height: 4rem;
    max-height: 50rem;
    background: inherit;
    border: 2px solid ${tokens.input.border};
    color: ${tokens.text.primary};

    &:focus {
        border-color: ${tokens.input.borderFocus};
        outline: none;
    }

    &:disabled {
        color: ${tokens.text.disabled};
    }

    &::placeholder {
        color: ${tokens.text.secondary};
    }

    &:disabled::placeholder {
        color: ${tokens.text.disabled};
    }

    &[aria-invalid='true'] {
        border-color: ${tokens.input.borderError};
    }
`;

type TextareaProps = TextareaAutosizeProps &
    Readonly<{
        showLengthIndicator?: boolean;
    }>;

const Textarea = (
    { showLengthIndicator, maxLength, value, onChange, ...rest }: TextareaProps,
    ref: React.Ref<HTMLTextAreaElement>,
) => {
    const [length, setLength] = useState(value?.toString().length ?? 0);

    const onTextChange: React.ChangeEventHandler<HTMLTextAreaElement> = (event) => {
        setLength(event.target.value.length);
        onChange?.(event);
    };

    return (
        <div>
            <StyledTextarea
                maxLength={maxLength}
                value={value}
                onChange={onTextChange}
                {...rest}
                ref={mergeRefs(ref, (el) => setLength(el?.value.length ?? 0))}
            />
            {showLengthIndicator && maxLength && (
                <SecondaryText className="f-12 flex-row flex-grow-1 justify-flex-end">
                    {length} / {maxLength}
                </SecondaryText>
            )}
        </div>
    );
};

const forwardRefTextarea = forwardRef(Textarea);

export { forwardRefTextarea as Textarea };
